import { User, type Nullable, type VtsLog } from "@/models"
import { useLoggedInUser } from "@/stores/coreDataStore"
import usePopups from "@/stores/popupsStore"
import { isReactive, shallowReactive, type InjectionKey, type Reactive } from "vue"
import { useService } from "vue-utils"
import { createBlankTemplateVtsLog } from "./createBlank"
import { canEditVtsLog } from "./permissions"
import { useVtsLogStore } from "./store"
import { useVtsValidation } from "./validation"
import { Temporal } from "temporal-polyfill"

export type VtsLogType = "create" | "edit"

export const VtsFormContextInjectionKey = Symbol("VtsFormContext") as InjectionKey<VtsFormContext>

export function useVtsFormContext(): VtsFormContext {
	return useService(VtsFormContextInjectionKey)
}

export class VtsFormContext {
	private readonly loggedInUser = useLoggedInUser()
	private readonly popups = usePopups()
	private vtsLogStore = useVtsLogStore()
	private validation = useVtsValidation()

	readonly logData: VtsLog

	public readonly existingLog: Nullable<VtsLog>

	constructor(existingLog: Nullable<Reactive<VtsLog>>) {
		if (existingLog && !isReactive(existingLog)) {
			throw new Error("existingLog must be reactive")
		}
		this.existingLog = existingLog as Nullable<VtsLog>
		this.logData = shallowReactive(createLogData(existingLog, this.loggedInUser))
	}

	get type(): VtsLogType {
		return this.existingLog === null ? "create" : "edit"
	}

	get canEdit() {
		return this.type === "create" || canEditVtsLog(this.existingLog, this.loggedInUser)
	}

	get saveButtonId() {
		return `vts-${this.type}-submit`
	}

	cancel() {
		return this.vtsLogStore.closeLogPopup()
	}

	async resetCreationForm() {
		const confirm = await this.validation.confirmReset()
		if(confirm)
			return this.vtsLogStore.resetCreationForm()
	}

	async save() {
		if (this.existingLog) {
			await this.vtsLogStore.updateVtsLog(this.logData)
		} else {
			this.logData.logTime = Temporal.Now.instant();
			this.logData.createdBy.timeStamp = Temporal.Now.instant();
			this.logData.editedBy.timeStamp = Temporal.Now.instant();
			await this.vtsLogStore.createVtsLog(this.logData)
			await this.vtsLogStore.resetCreationForm()
		}
	}

	handleSaveHotkey = (event: KeyboardEvent) => {
		if (!event.ctrlKey || event.key.toLowerCase() !== "s") return
		if (this.type === "create" && this.vtsLogStore.editingLog !== null) return

		event.preventDefault()
		if (this.popups.topPopup.value !== null) return
		if (event.repeat || !this.canEdit) return
	
		const button = document.getElementById(this.saveButtonId)
		if (button instanceof HTMLButtonElement) button.click()
	}
}

function createLogData(log: Nullable<VtsLog>, loggedInUser: User): VtsLog {
	if (log) return log
	return createBlankTemplateVtsLog(loggedInUser)
}
